import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Exchange } from '../../proto/generated/common'
import { getSecurityKey } from '../../util/security'
import type { RootState } from '../../store'
import AiCard from './cardKit/AiCard'
import { formatInquiryRupee, type PositionInquiry, type PositionInquirySnapshot } from './domains/tradingDomain'
import { formatPnlLine } from './orderPreviewDisplay'
import OrderTrackerActions from './OrderTrackerActions'
import { positionActions } from './trackerActions'

type Props = {
    inquiry: PositionInquiry
}

function parseExchange(value: string): Exchange {
    const key = value.toUpperCase()
    if (key === 'BSE') return Exchange.BSE
    if (key === 'MCX') return Exchange.MCX
    return Exchange.NSE
}

function useLtpRupees(exchange: string, code: string): number | null {
    const key = getSecurityKey(parseExchange(exchange), code)
    return useSelector((state: RootState) => {
        const book = state.securityStore.candleChangeBook?.[key]
        const snapshot = state.securityStore.snapshots[key]
        const multiplier = snapshot?.priceMultiplier ?? 100
        const raw = book?.ltp ?? snapshot?.lastTradedPrice ?? 0
        if (!raw || raw <= 0) return null
        return raw / multiplier
    })
}

function PositionRow({ position }: { position: PositionInquirySnapshot }) {
    const qty = Math.abs(position.netQuantity)
    const avg = formatInquiryRupee(position.averagePrice)
    const ltpRupees = useLtpRupees(position.exchange, position.securityCode)
    const realized = formatInquiryRupee(position.realizedPnl)

    let unrealizedLine: string | null = null
    if (ltpRupees != null && position.averagePrice != null && qty > 0) {
        const signedQty = position.netQuantity
        const pnl = (ltpRupees - position.averagePrice) * signedQty
        const cost = Math.abs(position.averagePrice * signedQty)
        const pct = cost > 0 ? (pnl / cost) * 100 : null
        unrealizedLine = formatPnlLine(pnl, pct)
    }

    return (
        <AiCard accent="neutral">
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>
                    {position.instrument.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {position.exchange}
                </Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.75 }} className="font-feature-num">
                {position.side} {qty}
                {avg ? ` @ ${avg}` : ''}
            </Typography>
            {unrealizedLine ? (
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mt: 0.5 }}
                    className={`font-feature-num ${
                        unrealizedLine.startsWith('+') ? 'green-text' : unrealizedLine.startsWith('−') ? 'red-text' : ''
                    }`}
                >
                    {unrealizedLine}
                </Typography>
            ) : null}
            {realized ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35 }} className="font-feature-num">
                    Realised {realized}
                </Typography>
            ) : null}
            <OrderTrackerActions actions={positionActions(position)} />
        </AiCard>
    )
}

export default function PositionInquiryCard({ inquiry }: Props) {
    if (inquiry.outcome !== 'MATCHED' || inquiry.positions.length === 0) {
        return null
    }
    return (
        <Stack spacing={0}>
            {inquiry.positions.map((position) => (
                <PositionRow
                    key={`${position.exchange}-${position.securityCode}-${position.product}`}
                    position={position}
                />
            ))}
        </Stack>
    )
}
